import '../../../sass/alpha-modules/optional/_leaflet-map.scss'

import 'leaflet-defaulticon-compatibility'
import 'leaflet-extra-markers'
import 'leaflet-gesture-handling'
import * as L from 'leaflet'
import AlphaFilters from '../../abstracts/classes/alpha-filters'
import AlphaHelpers from '../../abstracts/classes/alpha-helpers'
import AlphaModule from '../../abstracts/classes/alpha-module'
import cloneDeep from 'lodash-es/cloneDeep'
import isEqual from 'lodash-es/isEqual'
import merge from 'lodash-es/merge'

export default class LeafletMap extends AlphaModule {
  constructor(...args) {
    super(...args)

    this._mapsInfo = new Map()
    this._lastClickedLatLng = null
  }

  getName() {
    return 'leafletMap'
  }

  getDefaultConfig() {
    const popupPadding = 32
    return {
      mapsSelector: '.alpha-map',
      mapPaddingRatio: 0.1,
      useMarkerBounce: false,
      useMarkerCluster: false,
      resetLastClickedLatLngTimeout: 50,
      leafletConfig: {
        center: '42.6638665,2.9073319',
        gestureHandling: true,
        zoom: 12,
        zoomDelta: 0.5,
        zoomSnap: 0
      },
      placeConfig: {
        latLng: null,
        postId: 0,
        relatedElementId: null,
        icon: null,
        popup: null
      },
      popupOptions: {
        minWidth: 100,
        autoPanPadding: L.point(popupPadding, popupPadding)
      }
    }
  }

  _onLoad() {
    super._onLoad()

    this._loadMaps()
  }

  _loadMaps() {
    const maps = AlphaHelpers.smartQuerySelectorAll(this.getConfig().mapsSelector)
    const loadMapsAsync = async () => {
      for (const containerEl of maps) {
        // wait until each map is loaded, because they might load extra components (clusters and such)
        // eslint-disable-next-line no-await-in-loop
        await this.loadMap(containerEl)
      }
    }

    loadMapsAsync()
  }

  async loadMap(containerEl) {
    // check if map isn't already loaded
    if (containerEl.classList.contains('leaflet-container')) {
      return false
    }

    // check if map isn't already registered
    if (this._hasContainer(containerEl)) {
      return false
    }

    // try to merge config with inline config
    let config = this.getConfig()
    const leafletMapConfigAttrName = AlphaHelpers.getAlphaAttributeName('leaflet-map-config')
    const customConfig = AlphaHelpers.getParsedJsonAttribute(containerEl, leafletMapConfigAttrName)
    if (customConfig) {
      config = merge(config, customConfig)
    }

    // retrieve potential places
    const mapId = containerEl.id
    let places = []
    const placesDataEl = document.getElementById(`${mapId}-places`)
    if (placesDataEl) {
      const placesData = AlphaHelpers.getParsedJsonContent(placesDataEl.innerText)
      if (placesData) {
        places = this.getPlacesFromData(placesData, config)
      }
    }

    // update map center config with potential places
    config.leafletConfig.center = this._processCenter(config.leafletConfig.center, places)

    // load Leaflet Smooth Marker Bouncing if necessary
    if (config.useMarkerBounce) {
      await import(/* webpackChunkName: "leaflet-smooth-marker-bouncing" */ 'leaflet.smooth_marker_bouncing')
    }

    // load Leaflet Marker Cluster if necessary
    if (config.useMarkerCluster) {
      await import(/* webpackChunkName: "leaflet-marker-cluster" */ '../../vendors/_leaflet-marker-cluster')
    }

    // initialize map
    const map = L.map(containerEl, config.leafletConfig)
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
    }).addTo(map)

    let mapInfo = {
      map,
      config,
      places: [],
      markers: []
    }

    // prevent double tap on map
    map.on('click', (e) => this._doubleTapPrevented(e))

    // initialize markers if we have any place
    mapInfo = this._setMarkersFromPlaces(containerEl, mapInfo, places, false, true)

    // inform other components that this map is loaded
    this.dispatchEvent('map.loaded', {
      mapInfo
    })

    return mapInfo
  }

  getMapsInfo() {
    return this._mapsInfo
  }

  getMapInfoByContainerElement(containerEl) {
    const maps = this.getMapsInfo()
    if (maps.has(containerEl)) {
      return maps.get(containerEl)
    }

    return null
  }

  getLatLngFromString(rawValue) {
    if (typeof rawValue === 'string' && rawValue.length) {
      const expectedCoordinatesLength = 2
      const values = rawValue.split(',').map((v) => v.trim())
      if (values.length === expectedCoordinatesLength && !values.some((v) => !AlphaHelpers.isNumber(v))) {
        return L.latLng(values)
      }
    }

    return null
  }

  getPlacesFromData(placesData, mapConfig) {
    if (!mapConfig) {
      mapConfig = this.getConfig()
    }

    const places = []
    placesData.forEach((currentPlaceConfig, placeIndex) => {
      // clone initial place config (that way we don't alter global place config) and merge it with current config
      let placeConfig = cloneDeep(mapConfig.placeConfig)
      placeConfig = merge(placeConfig, currentPlaceConfig)

      // ensure we have valid latitude / longitude
      const latLng = this.getLatLngFromString(placeConfig.latLng)
      if (!latLng) {
        console.error(`Place #${placeIndex} has invalid or missing coordinates.`, placeConfig)
        return
      }

      placeConfig.latLng = latLng

      // allow filtering of place config
      placeConfig = AlphaFilters.applyFilters(this.filterName('place.config'), placeConfig, mapConfig)
      places.push(placeConfig)
    })

    return places
  }

  addMarkersFromPlacesData(containerEl, placesData) {
    return this._setMarkersFromPlacesData(containerEl, placesData, true)
  }

  setMarkersFromPlacesData(containerEl, placesData) {
    return this._setMarkersFromPlacesData(containerEl, placesData, false)
  }

  _setMarkersFromPlacesData(containerEl, placesData, append) {
    const mapInfo = this.getMapInfoByContainerElement(containerEl)
    let markers = []
    if (mapInfo) {
      const places = this.getPlacesFromData(placesData, mapInfo.config)
      markers = this._setMarkersFromPlaces(containerEl, mapInfo, places, append)
    }

    return markers
  }

  _setMarkersFromPlaces(containerEl, mapInfo, newPlaces, append, isInit = false) {
    const map = mapInfo?.map
    const config = mapInfo?.config
    let places = mapInfo?.places
    let markers = mapInfo?.markers
    let markersGroup = mapInfo?.markersGroup

    if (!markersGroup && config.useMarkerCluster) {
      markersGroup = L.markerClusterGroup()
    }

    if (!map || !config || !markers || !places) {
      throw new Error('Invalid mapInfo')
    }

    if (!append && markers.length) {
      if (config.useMarkerCluster) {
        markersGroup.clearLayers()
      } else {
        for (const marker of markers) {
          map.removeLayer(marker)
        }
      }

      places = []
      markers = []
    }

    if (newPlaces.length) {
      newPlaces.forEach((placeConfig) => {
        const marker = L.marker(placeConfig.latLng, {
          icon: L.ExtraMarkers.icon({
            icon: placeConfig.icon || 'fas fa-info',
            prefix: '',
            svg: true
          })
        })

        const postId = parseInt(placeConfig.postId, 10)
        marker._postId = postId

        // handle marker popup
        const hasInlinePopup = placeConfig.popup && placeConfig.popup.length
        if (hasInlinePopup) {
          const sanitizedPopup = AlphaHelpers.getSanitizedHtml(placeConfig.popup)
          if (sanitizedPopup.length) {
            marker.bindPopup(sanitizedPopup, config.popupOptions)
          }
        } else if (postId) {
          // listen to click on marker to query popup content
          const manager = this._getManager()
          marker.addEventListener('click', async (e) => {
            const clickedMarkerEl = e.target._icon
            const markerPopup = marker.getPopup()

            // load popup content if necessary
            if (!markerPopup && !clickedMarkerEl.classList.contains('alpha-loading-popup')) {
              // add loading effect on marker
              clickedMarkerEl.classList.add('alpha-loading-popup')

              const mapPopupContentUrl = `${manager.getConfig().ajaxUrl}?action=get_map_marker_popup_content&postId=${postId}`
              const response = await (await fetch(mapPopupContentUrl)).json()
              if (typeof response.content !== 'undefined') {
                marker.bindPopup(response.content, config.popupOptions).openPopup()
              }
            } else {
              markerPopup.openPopup()
            }
          })
        }

        // prevent double tap on marker
        marker.on('click', (e) => {
          // if there was a double tap, attempt to reopen popup
          // it might have been instantly closed by double tap...
          if (this._doubleTapPrevented(e)) {
            marker.openPopup()
          }
        })

        // handle marker bouncing effect if necessary
        if (placeConfig.relatedElementId && config.useMarkerBounce) {
          const relatedEl = document.getElementById(placeConfig.relatedElementId)
          if (relatedEl) {
            marker.setBouncingOptions({
              exclusive: true
            })

            relatedEl.addEventListener('mouseenter', () => marker.bounce())
            relatedEl.addEventListener('mouseleave', () => marker.stopBouncing())
          }
        }

        // add marker directly to map if we aren't using cluster
        if (!config.useMarkerCluster) {
          marker.addTo(map)
        }

        places.push(placeConfig)
        markers.push(marker)
      })

      // add marker cluster to map if necessary
      if (config.useMarkerCluster) {
        markersGroup.addLayers(markers)
        markersGroup.addTo(map)
      }

      // handle popup open / close events
      let activeMarkerEl = null
      map.addEventListener('popupopen', (e) => {
        // change marker color
        activeMarkerEl = e.popup._source._icon
        activeMarkerEl.classList.add('alpha-active-marker')

        // remove loading effect
        activeMarkerEl.classList.remove('alpha-loading-popup')
      })

      map.addEventListener('popupclose', () => {
        // reset marker color
        if (activeMarkerEl) {
          activeMarkerEl.classList.remove('alpha-active-marker')
          activeMarkerEl = null
        }
      })

      // post-processing, based on markers count
      if (markers.length === 1) {
        // try to open marker's popup when we only have one marker
        const firstMarker = markers[0]
        const icon = firstMarker?._icon
        if (icon) {
          icon.click()
        }

        // center map on point in case of a refresh
        if (!isInit) {
          map.setView(firstMarker.getLatLng(), config.leafletConfig.zoom)
        }
      } else {
        if (!markersGroup) {
          // eslint-disable-next-line new-cap
          markersGroup = new L.featureGroup(markers)
        }

        // fit map to markers
        map.fitBounds(markersGroup.getBounds().pad(config.mapPaddingRatio))
      }
    }

    // update map info
    mapInfo.places = places
    mapInfo.markers = markers
    if (config.useMarkerCluster) {
      mapInfo.markersGroup = markersGroup
    }

    this._addMapInfo(containerEl, mapInfo)

    // dispatch event if we aren't initializing the map
    if (!isInit) {
      this.dispatchEvent('markers.updated', {
        mapInfo
      })
    }

    return mapInfo
  }

  _addMapInfo(containerEl, mapInfo) {
    this._mapsInfo.set(containerEl, mapInfo)
  }

  _hasContainer(containerEl) {
    return this.getMapsInfo().has(containerEl)
  }

  _processCenter(defaultCenter, places) {
    let center = defaultCenter
    if (typeof center === 'string') {
      center = this.getLatLngFromString(center)
    }

    if (places.length) {
      center = places[0].latLng
    }

    return center
  }

  _doubleTapPrevented(e) {
    const latLng = e.latlng || e._latlng
    if (latLng) {
      if (isEqual(latLng, this._getLastClickedLatLng())) {
        L.DomEvent.stop(e)
        return true
      }

      this._setLastClickedLatLng(latLng)
    }

    return false
  }

  _getLastClickedLatLng() {
    return this._lastClickedLatLng
  }

  _setLastClickedLatLng(latLng) {
    this._lastClickedLatLng = latLng

    // reset coordinates after a small delay
    setTimeout(() => {
      this._lastClickedLatLng = null
    }, this.getConfig().resetLastClickedLatLngTimeout)
  }
}
